import React, { useRef, useEffect, useState } from 'react';
import { ReactComponent as IconClose } from '../../../../assets/img/icons/Close.svg';
import { Icon } from '../../../../common/components/ui/Icon/Icon';

interface BrandLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BrandLinkModal: React.FC<BrandLinkModalProps> = ({ isOpen, onClose }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasError, setHasError] = useState(false);

  const getIframeSrc = (): string => {
    const baseUrl = '/brandlink/';

    return `${baseUrl}?MainwindowMode=EmbeddedWorkplace#e1cib/app/Обработка.МФТИ_ПолучитьФайлыЗКГУпоСотрудникам`;
  };

  // Функция для закрытия с подтверждением
  const handleClose = () => {
    const isConfirmed = window.confirm('Вы уверены, что хотите закрыть окно? Все несохраненные данные могут быть потеряны.');

    if (isConfirmed) {
      onClose();
    }
  };

  // Функция для обновления iframe
  const handleRefresh = () => {
    if (!iframeRef.current) return;

    setIsRefreshing(true);
    setHasError(false);

    // Сбрасываем src для полного обновления
    iframeRef.current.src = '';

    // Небольшая задержка для гарантированного сброса
    setTimeout(() => {
      if (iframeRef.current) {
        // Используем новую функцию для получения актуального URL
        iframeRef.current.src = getIframeSrc();
      }
    }, 100);
  };

  // Обработчик загрузки iframe
  const handleIframeLoad = () => {
    setIsRefreshing(false);
    setHasError(false); // Сначала сбрасываем ошибку

    let attempts = 0;
    const maxAttempts = 10; // Максимум 10 попыток
    const checkInterval = 200; // Каждые 200ms

    const checkForClass = () => {
      attempts++;

      try {
        const iframe = iframeRef.current;
        if (!iframe) {
          console.error(`Попытка ${attempts}: iframe не найден`);
          if (attempts >= maxAttempts) setHasError(true);
          return;
        }

        // Пробуем получить доступ к содержимому
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;

        if (!iframeDoc) {
          console.log(`Попытка ${attempts}: документ недоступен`);
          if (attempts < maxAttempts) {
            setTimeout(checkForClass, checkInterval);
          } else {
            setHasError(true);
          }
          return;
        }

        // Проверяем, есть ли body
        if (!iframeDoc.body) {
          console.log(`Попытка ${attempts}: body не найден`);
          if (attempts < maxAttempts) {
            setTimeout(checkForClass, checkInterval);
          } else {
            setHasError(true);
          }
          return;
        }

        // Проверяем класс
        const hasClass = iframeDoc.body.classList?.contains('1cei')
          || iframeDoc.body.classList?.contains('main-form')
          || iframeDoc.title.toLowerCase().includes('1с')
        ;

        console.log(`Попытка ${attempts}:`, {
          hasClass: hasClass,
          bodyClass: iframeDoc.body.className,
          readyState: iframeDoc.readyState,
          documentElement: iframeDoc.documentElement?.className
        });

        if (hasClass) {
          console.log(`✅ Успех на попытке ${attempts}!`);
          setHasError(false);
        } else {
          // Проверяем другие возможные варианты
          const bodyClass = iframeDoc.body.className || '';
          if (bodyClass.includes('1cei')) {
            console.log(`✅ Найден через className на попытке ${attempts}!`);
            setHasError(false);
          } else if (attempts < maxAttempts) {
            console.log(`Попытка ${attempts}: класс не найден, пробуем еще...`);
            setTimeout(checkForClass, checkInterval);
          } else {
            console.error(`❌ Класс не найден после ${maxAttempts} попыток`);
            setHasError(true);
          }
        }

      } catch (error) {
        console.error(`Попытка ${attempts} завершилась ошибкой:`, error);

        if (attempts < maxAttempts) {
          setTimeout(checkForClass, checkInterval);
        } else {
          setHasError(true);
        }
      }
    };

    // Начинаем проверку
    setTimeout(checkForClass, 100);
  };

  // Обработчик ошибок iframe
  const handleIframeError = () => {
    setIsRefreshing(false);
    setHasError(true);
  };

  // Добавляем обработчик сообщений от iframe для обновления состояния
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Проверяем происхождение сообщения для безопасности
      if (event.origin !== window.location.origin) return;

      // Можно добавить обработку сообщений от iframe,
      // например, для автоматического обновления при сессионной ошибке
      if (event.data.type === 'SESSION_EXPIRED') {
        handleRefresh();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Блокировка скролла при открытии модального окна
  useEffect(() => {
    if (isOpen) {
      // Запоминаем текущее положение скролла
      const scrollY = window.scrollY;
      // Фиксируем тело документа
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflowY = 'hidden';

      return () => {
        // Восстанавливаем скролл при закрытии
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflowY = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Автоматическое обновление сессии при открытии модального окна
  useEffect(() => {
    if (isOpen && iframeRef.current) {
      // Небольшая задержка для инициализации iframe
      const timer = setTimeout(() => {
        handleRefresh();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000,
      }}
      onClick={handleClose}
    >
      <div
        style={{
          position: 'relative',
          width: '90%',
          height: '90%',
          backgroundColor: 'white',
          borderRadius: '8px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            height: '30px',
            backgroundColor: '#0078d4',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 10px',
            fontSize: '14px',
            fontWeight: 'normal',
            fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
            userSelect: 'none',
            cursor: 'default',
          }}
        >
          <span>BrandLink</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={handleRefresh}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'white',
                padding: '2px 8px',
                cursor: 'pointer',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              ↻ Обновить
            </button>
            <Icon
              onClick={handleClose}
              icon={IconClose}
              boxSize={20}
              iconSize={16}
            />
          </div>
        </div>

        <div style={{ position: 'relative', flex: 1 }}>
          {!hasError ? (
            <iframe
              ref={iframeRef}
              src={getIframeSrc()}
              title="BrandLink"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                opacity: isRefreshing ? 0.5 : 1,
                transition: 'opacity 0.3s',
              }}
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              allowFullScreen
            />
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '20px',
                textAlign: 'center',
              }}
            >
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                  Ошибка загрузки
                </div>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
                  Обновите страницу
                </div>
              </div>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#0078d4',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Обновить страницу
              </button>
            </div>
          )}

          {isRefreshing && !hasError && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                zIndex: 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span>Обновление сессии...</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};